import React from 'react'
import { Helmet } from 'react-helmet-async'

export default function ConfidentialitePage() {
  const pageTitle = "Politique de confidentialité | Léger's Coffee"
  const pageDescription =
    "Consultez la politique de confidentialité de Léger's Coffee : données collectées, finalités, durée de conservation, cookies, droits des utilisateurs et contact."
  const canonicalUrl = 'https://legerscoffee.fr/confidentialite'

  return (
    <>
      <Helmet>
        <html lang="fr" />
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Léger's Coffee" />
        <meta property="og:locale" content="fr_CH" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
      </Helmet>

      <main data-testid="confidentialite-page" className="min-h-screen pt-20 bg-warm-alabaster">
        <section className="bg-warm-secondary py-12 lg:py-16">
          <div className="max-w-5xl mx-auto px-6 lg:px-12">
            <span className="text-latte-gold text-sm uppercase tracking-widest font-medium mb-4 block">
              Protection des données
            </span>

            <h1 className="font-serif text-3xl lg:text-4xl text-espresso font-semibold">
              Politique de confidentialité
            </h1>

            <p className="text-soft-charcoal-secondary mt-3 max-w-3xl leading-relaxed">
              La présente politique de confidentialité explique comment Léger&apos;s Coffee collecte,
              utilise, conserve et protège les données personnelles des utilisateurs du site, des clients
              et des personnes qui prennent contact avec l&apos;établissement.
            </p>
          </div>
        </section>

        <section className="py-12 lg:py-16">
          <div className="max-w-5xl mx-auto px-6 lg:px-12">
            <div className="bg-white border border-border-subtle p-6 lg:p-10 space-y-10">
              <section>
                <h2 className="font-serif text-2xl text-espresso font-semibold mb-4">
                  1. Responsable du traitement
                </h2>

                <div className="space-y-2 text-soft-charcoal-secondary leading-relaxed">
                  <p>
                    Le responsable du traitement des données personnelles collectées sur le site est :
                  </p>
                  <p>
                    <strong className="text-espresso">Léger&apos;s Coffee</strong>
                  </p>
                  <p>
                    <strong className="text-espresso">Adresse :</strong> Pl. des Augustins 19, 1205 Genève, Suisse
                  </p>
                  <p>
                    <strong className="text-espresso">Email :</strong> contact@legers-coffee.com
                  </p>
                  <p>
                    <strong className="text-espresso">Téléphone :</strong> +41 22 123 45 67
                  </p>
                </div>
              </section>

              <section className="pt-8 border-t border-border-subtle">
                <h2 className="font-serif text-2xl text-espresso font-semibold mb-4">
                  2. Données collectées
                </h2>

                <div className="space-y-4 text-soft-charcoal-secondary leading-relaxed">
                  <p>
                    Dans le cadre de l&apos;utilisation du site et des services proposés, Léger&apos;s Coffee
                    peut collecter différentes catégories de données personnelles, notamment :
                  </p>

                  <ul className="space-y-2 list-disc pl-5">
                    <li>les données d&apos;identité, comme le prénom et le nom ;</li>
                    <li>les coordonnées, comme l&apos;adresse email et le numéro de téléphone ;</li>
                    <li>les informations liées au compte client ;</li>
                    <li>les informations liées aux commandes, à leur historique et à leur suivi ;</li>
                    <li>les préférences de communication, notamment l&apos;inscription à la newsletter ;</li>
                    <li>les informations transmises via les formulaires de contact ;</li>
                    <li>les données techniques et de navigation, comme l&apos;adresse IP, le navigateur, l&apos;appareil utilisé et certaines données de cookies.</li>
                  </ul>
                </div>
              </section>

              <section className="pt-8 border-t border-border-subtle">
                <h2 className="font-serif text-2xl text-espresso font-semibold mb-4">
                  3. Modes de collecte
                </h2>

                <div className="space-y-4 text-soft-charcoal-secondary leading-relaxed">
                  <p>Les données peuvent être collectées lorsque vous :</p>

                  <ul className="space-y-2 list-disc pl-5">
                    <li>créez un compte sur le site ;</li>
                    <li>passez une commande ;</li>
                    <li>vous inscrivez à la newsletter ;</li>
                    <li>remplissez un formulaire de contact ;</li>
                    <li>naviguez sur le site ;</li>
                    <li>échangez avec Léger&apos;s Coffee par email ou par téléphone.</li>
                  </ul>
                </div>
              </section>

              <section className="pt-8 border-t border-border-subtle">
                <h2 className="font-serif text-2xl text-espresso font-semibold mb-4">
                  4. Finalités du traitement
                </h2>

                <div className="space-y-4 text-soft-charcoal-secondary leading-relaxed">
                  <p>Les données personnelles sont utilisées pour les finalités suivantes :</p>

                  <ul className="space-y-2 list-disc pl-5">
                    <li>gérer les comptes clients ;</li>
                    <li>traiter, confirmer et suivre les commandes ;</li>
                    <li>assurer le service client et répondre aux demandes ;</li>
                    <li>envoyer des emails liés au fonctionnement du compte ou de la commande ;</li>
                    <li>adresser des actualités, offres ou communications commerciales lorsque vous y avez consenti ;</li>
                    <li>améliorer le site, les services et l&apos;expérience utilisateur ;</li>
                    <li>garantir la sécurité du site et prévenir les abus ou fraudes ;</li>
                    <li>respecter les obligations légales, comptables et administratives applicables.</li>
                  </ul>
                </div>
              </section>

              <section className="pt-8 border-t border-border-subtle">
                <h2 className="font-serif text-2xl text-espresso font-semibold mb-4">
                  5. Base légale du traitement
                </h2>

                <div className="space-y-4 text-soft-charcoal-secondary leading-relaxed">
                  <p>Les traitements de données reposent notamment sur :</p>

                  <ul className="space-y-2 list-disc pl-5">
                    <li>l&apos;exécution d&apos;un contrat ou de mesures précontractuelles, par exemple pour gérer une commande ;</li>
                    <li>le consentement, notamment pour l&apos;inscription à la newsletter ;</li>
                    <li>l&apos;intérêt légitime de Léger&apos;s Coffee, par exemple pour améliorer le service, sécuriser le site ou répondre aux demandes ;</li>
                    <li>le respect d&apos;obligations légales.</li>
                  </ul>
                </div>
              </section>

              <section className="pt-8 border-t border-border-subtle">
                <h2 className="font-serif text-2xl text-espresso font-semibold mb-4">
                  6. Destinataires des données
                </h2>

                <div className="space-y-4 text-soft-charcoal-secondary leading-relaxed">
                  <p>
                    Les données personnelles sont destinées aux personnes autorisées au sein de Léger&apos;s Coffee
                    ainsi qu&apos;aux prestataires techniques strictement nécessaires au fonctionnement du site et des services.
                  </p>

                  <p>Ces destinataires peuvent inclure notamment :</p>

                  <ul className="space-y-2 list-disc pl-5">
                    <li>les outils d&apos;hébergement et d&apos;infrastructure ;</li>
                    <li>les services de gestion de base de données ;</li>
                    <li>les prestataires de paiement ;</li>
                    <li>les services d&apos;envoi d&apos;emails ;</li>
                    <li>les outils d&apos;analyse d&apos;audience ou de performance ;</li>
                    <li>les prestataires de support ou de maintenance technique.</li>
                  </ul>

                  <p>
                    Les données ne sont pas vendues à des tiers.
                  </p>
                </div>
              </section>

              <section className="pt-8 border-t border-border-subtle">
                <h2 className="font-serif text-2xl text-espresso font-semibold mb-4">
                  7. Durée de conservation
                </h2>

                <div className="space-y-4 text-soft-charcoal-secondary leading-relaxed">
                  <p>
                    Les données personnelles sont conservées pendant une durée n&apos;excédant pas celle nécessaire
                    aux finalités pour lesquelles elles ont été collectées, sous réserve des obligations légales
                    de conservation.
                  </p>

                  <ul className="space-y-2 list-disc pl-5">
                    <li>les données de compte sont conservées tant que le compte est actif, puis pendant une durée raisonnable de gestion ou d&apos;archivage ;</li>
                    <li>les données de commande peuvent être conservées pour assurer le suivi client, la gestion administrative et les obligations comptables ;</li>
                    <li>les données liées à la newsletter sont conservées jusqu&apos;au retrait du consentement ou à la désinscription ;</li>
                    <li>les demandes de contact peuvent être conservées pendant le temps nécessaire au traitement et au suivi de la demande ;</li>
                    <li>les données techniques ou de mesure d&apos;audience sont conservées selon la durée applicable aux outils utilisés.</li>
                  </ul>
                </div>
              </section>

              <section className="pt-8 border-t border-border-subtle">
                <h2 className="font-serif text-2xl text-espresso font-semibold mb-4">
                  8. Sécurité des données
                </h2>

                <div className="space-y-4 text-soft-charcoal-secondary leading-relaxed">
                  <p>
                    Léger&apos;s Coffee met en œuvre des mesures techniques et organisationnelles raisonnables
                    destinées à protéger les données personnelles contre la perte, l&apos;accès non autorisé,
                    l&apos;altération, la divulgation ou l&apos;utilisation abusive.
                  </p>

                  <p>
                    Malgré ces précautions, aucun système n&apos;offre une sécurité absolue. Les utilisateurs
                    sont invités à protéger leurs identifiants de connexion et à signaler sans délai toute activité suspecte.
                  </p>
                </div>
              </section>

              <section className="pt-8 border-t border-border-subtle">
                <h2 className="font-serif text-2xl text-espresso font-semibold mb-4">
                  9. Cookies et technologies similaires
                </h2>

                <div className="space-y-4 text-soft-charcoal-secondary leading-relaxed">
                  <p>
                    Le site peut utiliser des cookies, traceurs ou technologies similaires pour assurer son bon fonctionnement,
                    mémoriser certaines préférences, mesurer l&apos;audience et améliorer l&apos;expérience utilisateur.
                  </p>

                  <p>
                    Certains cookies peuvent être strictement nécessaires au fonctionnement du site, tandis que d&apos;autres
                    peuvent servir à des fins statistiques, analytiques ou de performance.
                  </p>

                  <p>
                    Vous pouvez configurer votre navigateur afin de refuser tout ou partie des cookies. Le refus de certains
                    cookies peut toutefois limiter certaines fonctionnalités du site.
                  </p>
                </div>
              </section>

              <section className="pt-8 border-t border-border-subtle">
                <h2 className="font-serif text-2xl text-espresso font-semibold mb-4">
                  10. Newsletter et communications commerciales
                </h2>

                <div className="space-y-4 text-soft-charcoal-secondary leading-relaxed">
                  <p>
                    Si vous vous inscrivez à la newsletter, votre adresse email pourra être utilisée pour vous envoyer
                    des actualités, informations commerciales, nouveautés et offres promotionnelles liées à Léger&apos;s Coffee.
                  </p>

                  <p>
                    Vous pouvez retirer votre consentement à tout moment, notamment en utilisant le lien de désinscription
                    présent dans les communications concernées ou en contactant Léger&apos;s Coffee.
                  </p>
                </div>
              </section>

              <section className="pt-8 border-t border-border-subtle">
                <h2 className="font-serif text-2xl text-espresso font-semibold mb-4">
                  11. Droits des utilisateurs
                </h2>

                <div className="space-y-4 text-soft-charcoal-secondary leading-relaxed">
                  <p>
                    Sous réserve du droit applicable, vous pouvez disposer de droits relatifs à vos données personnelles,
                    notamment :
                  </p>

                  <ul className="space-y-2 list-disc pl-5">
                    <li>le droit d&apos;accès ;</li>
                    <li>le droit de rectification ;</li>
                    <li>le droit de suppression ;</li>
                    <li>le droit d&apos;opposition ;</li>
                    <li>le droit à la limitation du traitement ;</li>
                    <li>le droit au retrait du consentement lorsque le traitement repose sur celui-ci.</li>
                  </ul>

                  <p>
                    Toute demande peut être adressée à :
                    <span className="text-espresso font-medium"> contact@legers-coffee.com</span>.
                  </p>
                </div>
              </section>

              <section className="pt-8 border-t border-border-subtle">
                <h2 className="font-serif text-2xl text-espresso font-semibold mb-4">
                  12. Transferts de données
                </h2>

                <div className="space-y-4 text-soft-charcoal-secondary leading-relaxed">
                  <p>
                    Certains prestataires techniques utilisés par Léger&apos;s Coffee peuvent être situés en dehors de la Suisse
                    ou du pays de consultation du site. Lorsque de tels transferts existent, ils sont encadrés dans la mesure
                    requise par les règles applicables.
                  </p>
                </div>
              </section>

              <section className="pt-8 border-t border-border-subtle">
                <h2 className="font-serif text-2xl text-espresso font-semibold mb-4">
                  13. Mise à jour de la politique
                </h2>

                <div className="space-y-4 text-soft-charcoal-secondary leading-relaxed">
                  <p>
                    Léger&apos;s Coffee peut modifier la présente politique de confidentialité à tout moment afin de refléter
                    une évolution légale, réglementaire, technique ou opérationnelle. La version en vigueur est celle publiée
                    sur cette page à la date de sa dernière mise à jour.
                  </p>
                </div>
              </section>

              <section className="pt-8 border-t border-border-subtle">
                <h2 className="font-serif text-2xl text-espresso font-semibold mb-4">
                  14. Contact
                </h2>

                <p className="text-soft-charcoal-secondary leading-relaxed">
                  Pour toute question relative à la présente politique de confidentialité ou à l&apos;utilisation de vos données
                  personnelles, vous pouvez contacter Léger&apos;s Coffee à l&apos;adresse suivante :
                  <span className="text-espresso font-medium"> contact@legers-coffee.com</span>.
                </p>
              </section>

              <div className="pt-8 border-t border-border-subtle">
                <p className="text-sm text-soft-charcoal-secondary">
                  Dernière mise à jour : mars 2026
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  )
}